import React, { useState, useEffect } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import {
  useGetSegmentByIdQuery,
  useCreateSegmentMutation,
  useEditSegmentMutation,
} from "../../../services";

const Segments: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { segmentid } = useParams();

  const {
    data: segmentResponse,
    isLoading: isSegmentLoading,
    isError: isSegmentError,
  } = useGetSegmentByIdQuery(segmentid as string, {
    skip: !segmentid,
  });

  const [createSegment, { isLoading: isCreating }] = useCreateSegmentMutation();
  const [editSegment, { isLoading: isUpdating }] = useEditSegmentMutation();

  const isSaving = isCreating || isUpdating;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "Active",
  });

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Hydrate form when segment detail loads (edit mode)
  useEffect(() => {
    if (!segmentResponse?.result) return;
    const s = segmentResponse.result as any;
    setFormData({
      name: s?.name ?? "",
      description: s?.description ?? "",
      status: s?.status ?? "Active",
    });
  }, [segmentResponse]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (segmentid) {
        await editSegment({
          id: segmentid,
          data: {
            name: formData.name.trim(),
            description: formData.description.trim(),
          },
        }).unwrap();
      } else {
        await createSegment({
          name: formData.name.trim(),
          description: formData.description.trim(),
        }).unwrap();
      }
      navigate(adminRoutes.segmentsList);
    } catch (err) {
      console.error("Save segment failed:", err);
    }
  };

  // Sidebar compensation
  const isSidebarOpen = useAppSelector(
    (state: RootState) => state.navbar.isOpen,
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  if (segmentid && isSegmentLoading) {
    return (
      <div
        className={`pt-20 text-center ${
          theme === "dark" ? "text-white" : "text-gray-900"
        }`}
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        Loading segment...
      </div>
    );
  }

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${
        theme === "dark" ? "bg-black" : "bg-gray-50"
      }`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      {/* Header */}
      <div className="mb-6">
        <h1
          className={`text-2xl font-semibold ${
            theme === "dark" ? "text-gray-300" : "text-gray-800"
          }`}
        >
          {segmentid ? "Edit Segment" : "Create Segment"}
        </h1>
        <div className="flex items-center gap-2 mt-2 text-sm">
          <Link
            to={adminRoutes.segmentsList}
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            All Segments
          </Link>
          <span
            className={theme === "dark" ? "text-gray-400" : "text-gray-600"}
          >
            /
          </span>
          <span
            className={theme === "dark" ? "text-[#B6CB24]" : "text-lime-600"}
          >
            {segmentid ? "Edit Segment" : "Create Segment"}
          </span>
        </div>
      </div>

      <div
        className={`${
          theme === "dark"
            ? "bg-[#1E1E1E] border border-gray-800"
            : "bg-white border border-gray-200"
        } rounded-lg p-6`}
      >
        <div className="mb-6">
          <h2
            className={`text-base font-medium border-b pb-2 border-gray-700 ${
              theme === "dark" ? "text-white" : "text-gray-800"
            }`}
          >
            Segment Information
          </h2>
        </div>

        {isSegmentError && (
          <p className="text-red-500 mb-4">Failed to load segment details.</p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Name */}
            <div>
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-700"
                }`}
              >
                Segment Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter segment name"
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  errors.name
                    ? "border border-red-500"
                    : theme === "dark"
                      ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                      : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
              )}
            </div>

            {/* Status */}
            {/* <div>
                            <label
                                className={`block text-xs font-normal mb-2 ${theme === "dark" ? "text-gray-400" : "text-gray-700"
                                    }`}
                            >
                                Status
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className={`w-full px-3 py-2.5 rounded-md text-sm ${theme === "dark"
                                    ? "bg-black border border-gray-800 text-white"
                                    : "bg-white border border-gray-300 text-gray-900"
                                    } focus:outline-none focus:ring-1 focus:ring-lime-500`}
                            >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                            </select>
                        </div> */}

            {/* Description */}
            <div className="md:col-span-2">
              <label
                className={`block text-xs font-normal mb-2 ${
                  theme === "dark" ? "text-gray-400" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter segment description"
                rows={4}
                className={`w-full px-3 py-2.5 rounded-md text-sm ${
                  theme === "dark"
                    ? "bg-black border border-gray-800 text-white placeholder-gray-600"
                    : "bg-white border border-gray-300 text-gray-900 placeholder-gray-400"
                } focus:outline-none focus:ring-1 focus:ring-lime-500`}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="px-8 py-2 rounded-md bg-[#B6CB24] text-black font-medium hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
              }}
            >
              {isSaving ? "Saving..." : segmentid ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => navigate(adminRoutes.segmentsList)}
              className={`px-8 py-2 rounded-md font-medium ${
                theme === "dark"
                  ? "bg-[#2C2C2C] text-gray-200 hover:bg-[#333333]"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Segments;
